import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useRoutes } from '../../../../hooks/useRoutes';
import ContentBody from '../../../../layout/content/ContentBody';
import ContentHeader from '../../../../layout/content/ContentHeader';
import AllAssignments from './components/Assignments';

import Breadcrumbs from '@pelckmans/elementis/components/breadcrumbs';
import Page from '@pelckmans/elementis/components/page';
import Tabs from '@pelckmans/elementis/components/tabs';
import { useContext } from 'react';
import AggregatedModuleAlerts from '../../../../shared-components/alerts/AggregatedModuleAlerts';
import ModuleContext from '../../_contexts/ModuleContext';
import SuperModuleBreadcrumb from '../../components/breadcrumbs/SuperModuleBreadcrumb';

const TABS = {
  TO_MAKE: 'TO_MAKE',
  FINISHED: 'FINISHED',
} as const;

type TabId = typeof TABS[keyof typeof TABS];

type AssignmentRouteKey = 'MATERIAL.ASSIGNMENTS.TO_MAKE' | 'MATERIAL.ASSIGNMENTS.FINISHED';

function Assignments() {
  const { t } = useTranslation();
  const location = useLocation();
  const { getRoute } = useRoutes();
  const navigate = useNavigate();
  const { allModules } = useContext(ModuleContext);

  const selectedTab: TabId | undefined = (Object.values(TABS) as TabId[]).find(tab =>
    location.pathname.includes(getRoute(`MATERIAL.ASSIGNMENTS.${tab}` as AssignmentRouteKey)),
  );

  const onTabChange = (tab: string) => {
    if (tab === selectedTab) return;

    navigate(getRoute(`MATERIAL.ASSIGNMENTS.${tab}` as AssignmentRouteKey));
  };

  return (
    <Page>
      <Tabs selectedTab={selectedTab} onTabChange={onTabChange}>
        <ContentHeader
          cover={
            <Page.Header
              breadcrumbs={
                <Breadcrumbs aria-label={t('MODULE_BREADCRUMB.A11Y_LABEL')}>
                  <SuperModuleBreadcrumb />
                </Breadcrumbs>
              }
              headerText={t('PAGES.ASSIGNMENTS.PAGE_TITLE')}
              tabs={
                <Tabs.TabList aria-label={t('PAGES.ASSIGNMENTS.TAB_LIST.A11Y_LABEL')}>
                  <Tabs.Tab id={TABS.TO_MAKE}>{t('PAGES.ASSIGNMENTS.STUDENT.TAB_LIST.TABS.TO_MAKE')}</Tabs.Tab>
                  <Tabs.Tab id={TABS.FINISHED}>{t('PAGES.ASSIGNMENTS.STUDENT.TAB_LIST.TABS.FINISHED')}</Tabs.Tab>
                </Tabs.TabList>
              }
            />
          }
        />
        <ContentBody
          scrollableTable
          alerts={<AggregatedModuleAlerts selectedModules={allModules} showLicenseAlertsOnly />}
        >
          <Tabs.TabsContent>
            {(Object.values(TABS) as TabId[]).map(tabId => {
              return (
                <Tabs.TabPanel key={tabId} id={tabId}>
                  <Outlet />
                </Tabs.TabPanel>
              );
            })}
          </Tabs.TabsContent>
        </ContentBody>
      </Tabs>
    </Page>
  );
}

export function StudentAssignmentTabContent({ tab }: { tab: string }) {
  return <AllAssignments finished={tab === TABS.FINISHED} />;
}

export default Assignments;
